//------ 删除后轮廓闭合重判 开始------
import {SketchEntityDeleteService} from '../../../sketch/delete';
//------ 删除后轮廓闭合重判 结束------
import {createSketchDimensionHistoryPatch} from '../../../history/dimension/SketchDimensionHistoryAdapter';
import type {EntityPatch} from '../../../history/types';
import {createExistingHistorySnapshot, createMissingHistorySnapshot} from '../../../history/types';
import type {SketchDimensionRepository} from '../../../sketch/dimension/repository/SketchDimensionRepository';
import type {SketchDimensionSnapshot} from '../../../sketch/dimension/SketchDimensionTypes';
import type {ContextMenuOpenEventPayload} from '../ContextMenuController';
import type {Viewer} from '../../Viewer';
import {AbstractContextMenuDeleteHandler} from './AbstractContextMenuDeleteHandler';

/**
 * 草图模式选择删除处理器。
 *
 * 设计说明：
 * 1. 右键菜单与 Delete 键共用本处理器，实体和显式选中尺寸必须在同一事务内删除；
 * 2. 具体草图对象删除、约束清理和轮廓闭合重判统一下沉到 `SketchEntityDeleteService`；
 * 3. 直接删除草图实体时，引用该实体或其级联删除控制点的智能尺寸同步删除；尺寸补丁经
 *    `resolveExtraPatches` 与运行时快照补丁合成同一笔事务（HIST-001）；
 * 4. 本类负责选择上下文到实体/尺寸领域删除能力的事务编排，不重新实现几何删除规则。
 */
export class SketchContextMenuDeleteHandler extends AbstractContextMenuDeleteHandler {
    //------ 删除后轮廓闭合重判 开始------
    /** 草图实体删除服务。 */
    private readonly sketchEntityDeleteService = new SketchEntityDeleteService();
    //------ 删除后轮廓闭合重判 结束------

    /**
     * 构造函数。
     *
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        super(viewer);
    }

    /**
     * 执行草图模式删除。
     *
     * @param context 菜单上下文快照。
     * @returns 删除动作是否执行成功。
     */
    protected executeInternal(context: ContextMenuOpenEventPayload): boolean {
        /** 本次删除产生的尺寸删除补丁（与运行时快照补丁同一笔事务）。 */
        let dimensionPatches: readonly EntityPatch[] = [];
        /** 删除开始前的完整草图尺寸状态，用于同步或异步历史提交失败回滚。 */
        let dimensionStateBefore: readonly SketchDimensionSnapshot[] = [];
        /** 删除完成后的完整草图尺寸状态，用于阻止异步失败回滚覆盖后续用户编辑。 */
        let dimensionStateAfter: readonly SketchDimensionSnapshot[] = [];
        /** 本次事务实际修改的尺寸仓库。 */
        let dimensionRepository: SketchDimensionRepository | null = null;
        /** 本次事务所属草图持久 ID。 */
        let dimensionSketchId: string | null = null;

        const removed = this.viewer.executeActiveSketchRuntimeHistoryTransaction({
            label: '删除草图选择',
            commandType: 'sketch.runtime.delete.selection',
            work: () => {
                const activeSketchGroup = this.viewer.sketchGroupManager?.activeSketchGroup ?? null;
                if (!activeSketchGroup) {
                    return false;
                }

                const repository = this.viewer.sketchGroupManager?.dimensionRepository ?? null;
                dimensionRepository = repository;
                dimensionSketchId = activeSketchGroup.persistentId;
                dimensionStateBefore = repository
                    ? repository.listBySketch(activeSketchGroup.persistentId)
                        .map((dimension) => dimension.toSnapshot())
                    : [];

                try {
                    /**
                     * 显式尺寸先删除，避免随后扫描关联尺寸时重复生成删除补丁；尺寸仓库事件
                     * 会同步摘除标注渲染器与运行时关系视图中的对应对象。
                     */
                    const explicitDimensionDeletePatches = repository
                        ? this.deleteDimensions(
                            repository,
                            activeSketchGroup.persistentId,
                            context.selectedSketchDimensionIds,
                        )
                        : [];
                    const deleteResult = context.selectedObjects.length > 0
                        ? this.sketchEntityDeleteService.deleteEntities({
                            sketchGroup: activeSketchGroup,
                            objects: [...context.selectedObjects],
                            syncContour: this.viewer.shadedSketchContoursEnabled,
                        })
                        : null;
                    const associatedDimensionDeletePatches = repository && deleteResult
                        && deleteResult.deletedPersistentIds.length > 0
                        ? this.deleteDimensionsReferencingEntities(
                            repository,
                            activeSketchGroup.persistentId,
                            new Set(deleteResult.deletedPersistentIds),
                        )
                        : [];

                    dimensionPatches = [
                        ...explicitDimensionDeletePatches,
                        ...associatedDimensionDeletePatches,
                    ];
                    dimensionStateAfter = repository
                        ? repository.listBySketch(activeSketchGroup.persistentId)
                            .map((dimension) => dimension.toSnapshot())
                        : [];

                    return dimensionPatches.length > 0
                        || (deleteResult?.deletedObjectIds.length ?? 0) > 0;
                } catch (error) {
                    // work 抛错时 Viewer 尚拿不到 result，尺寸仓库必须在领域适配器内部先行恢复。
                    if (repository) {
                        this.restoreDimensionState(
                            repository,
                            activeSketchGroup.persistentId,
                            dimensionStateBefore,
                        );
                        this.viewer.selectSketchDimensionsProgrammatically?.(
                            context.selectedSketchDimensionIds,
                        );
                    }
                    throw error;
                }
            },
            shouldCommit: (removed) => removed,
            resolveExtraPatches: () => dimensionPatches,
            rollbackExtraState: () => {
                if (
                    dimensionRepository
                    && dimensionSketchId
                    && this.isDimensionStateCurrent(
                        dimensionRepository,
                        dimensionSketchId,
                        dimensionStateAfter,
                    )
                ) {
                    this.restoreDimensionState(
                        dimensionRepository,
                        dimensionSketchId,
                        dimensionStateBefore,
                    );
                    this.viewer.selectSketchDimensionsProgrammatically?.(
                        context.selectedSketchDimensionIds,
                    );
                }
            },
        });

        if (removed) {
            // 删除成功后统一清空尺寸选择；实体选择由基类在同一动作模板中清理。
            this.viewer.selectSketchDimensionsProgrammatically?.([]);
        }

        return removed;
    }

    /**
     * 删除当前草图中的指定智能尺寸并构造历史补丁。
     *
     * @param repository 尺寸仓库。
     * @param sketchId 当前活动草图持久 ID。
     * @param dimensionIds 待删除尺寸的持久 ID。
     * @returns 尺寸删除历史补丁。
     */
    private deleteDimensions(
        repository: SketchDimensionRepository,
        sketchId: string,
        dimensionIds: readonly string[],
    ): EntityPatch[] {
        const patches: EntityPatch[] = [];

        [...new Set(dimensionIds)].forEach((dimensionId) => {
            if (!repository.get(sketchId, dimensionId)) {
                return;
            }
            const before = repository.delete(sketchId, dimensionId);

            patches.push(createSketchDimensionHistoryPatch({
                entityId: dimensionId,
                operation: 'delete',
                before: createExistingHistorySnapshot(before),
                after: createMissingHistorySnapshot(),
                metadata: {sketchId},
            }));
        });

        return patches;
    }

    /**
     * 删除引用已删实体或控制点的全部智能尺寸。
     *
     * 一个尺寸可能引用多个草图对象；任一引用命中已删除持久 ID 后，该尺寸的求解定义已经
     * 不完整，因此必须整体删除。显式选中的尺寸已在实体删除前摘除，本扫描只处理剩余尺寸，
     * 从源头保证同一尺寸不会生成重复历史补丁。
     *
     * @param repository 尺寸仓库。
     * @param sketchId 当前活动草图持久 ID。
     * @param deletedEntityIds 已删除实体与级联删除控制点的持久 ID 集合。
     * @returns 关联尺寸删除历史补丁。
     */
    private deleteDimensionsReferencingEntities(
        repository: SketchDimensionRepository,
        sketchId: string,
        deletedEntityIds: ReadonlySet<string>,
    ): EntityPatch[] {
        const associatedDimensionIds = repository.listBySketch(sketchId)
            .filter((dimension) => dimension.toSnapshot().references.some((reference) => (
                deletedEntityIds.has(reference.entityId)
                || (
                    typeof reference.pointId === 'string'
                    && deletedEntityIds.has(reference.pointId)
                )
            )))
            .map((dimension) => dimension.dimensionId);

        // 统一复用尺寸删除入口，确保仓库事件和 Undo/Redo 补丁保持一致。
        return this.deleteDimensions(
            repository,
            sketchId,
            associatedDimensionIds,
        );
    }

    /**
     * 把单个草图的尺寸仓库恢复到事务开始前状态。
     *
     * 先移除事务中新建或残留的尺寸，再覆盖恢复全部旧快照；仓库事件会同步驱动标注渲染器
     * 与尺寸运行时关系视图回到一致状态。
     *
     * @param repository 尺寸仓库。
     * @param sketchId 当前活动草图持久 ID。
     * @param snapshots 事务开始前的完整尺寸快照。
     */
    private restoreDimensionState(
        repository: SketchDimensionRepository,
        sketchId: string,
        snapshots: readonly SketchDimensionSnapshot[],
    ): void {
        const beforeIds = new Set(snapshots.map((snapshot) => snapshot.dimensionId));

        repository.listBySketch(sketchId).forEach((dimension) => {
            if (!beforeIds.has(dimension.dimensionId)) {
                repository.removeDimensionForRestore(dimension.dimensionId);
            }
        });
        snapshots.forEach((snapshot) => {
            repository.restoreDimension(snapshot);
        });
    }

    /**
     * 判断尺寸仓库是否仍保持本删除事务完成后的状态。
     *
     * 历史提交允许返回 Promise；如果等待期间用户又完成了新的尺寸编辑，旧事务失败时不得
     * 用旧 before 覆盖新状态。尺寸删除不是热路径，使用确定性快照序列化比较可保持实现直接。
     *
     * @param repository 尺寸仓库。
     * @param sketchId 当前活动草图持久 ID。
     * @param expectedSnapshots 本删除事务完成后的尺寸快照。
     * @returns 仓库仍处于预期 after 状态时返回 true。
     */
    private isDimensionStateCurrent(
        repository: SketchDimensionRepository,
        sketchId: string,
        expectedSnapshots: readonly SketchDimensionSnapshot[],
    ): boolean {
        const currentSnapshots = repository.listBySketch(sketchId)
            .map((dimension) => dimension.toSnapshot())
            .sort((left, right) => left.dimensionId.localeCompare(right.dimensionId));
        const normalizedExpectedSnapshots = [...expectedSnapshots]
            .sort((left, right) => left.dimensionId.localeCompare(right.dimensionId));

        return JSON.stringify(currentSnapshots) === JSON.stringify(normalizedExpectedSnapshots);
    }
}
